import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { arrow } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { useState } from "react";

type SolutionSummaryProps = {
  rating: number | string;
  chemistry: number | string;
  owned: number | string;
  untradeables: number | string;
  duplicates: number | string;
  isMobile?: boolean;
};

export const SolutionSummary = ({
  rating,
  chemistry,
  owned,
  untradeables,
  duplicates,
  isMobile,
}: SolutionSummaryProps) => {
  const [open, setOpen] = useState(false);

  const items = [
    {
      label: "Rating",
      value: rating,
      icon: (
        <img
          src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/rating.png`}
          alt="rating"
        />
      ),
      tooltip: "Rating",
    },
    {
      label: "Chemistry",
      value: chemistry,
      icon: (
        <img
          src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/chemistry.png`}
          alt="chemistry"
        />
      ),
      tooltip: "Chemistry",
    },
    {
      label: "Owned",
      value: owned,
      icon: (
        <img
          src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/owned.png`}
          alt="owned"
        />
      ),
      tooltip: "Owned Players",
    },
    {
      label: "Untradables",
      value: untradeables,
      icon: (
        <img
          src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/untradeable.png`}
          alt="untradeable"
        />
      ),
      tooltip: "Untradable Players",
    },
    {
      label: "Duplicates",
      value: duplicates,
      icon: (
        <img
          src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/sbc-icons/duplicate.png`}
          alt="duplicate"
        />
      ),
      tooltip: "Duplicate Players",
    },
  ];

  if (isMobile) {
    const firstGroup = items.slice(0, 2);
    const secondGroup = items.slice(2);

    return (
      <div className="flex justify-between items-center w-full h-20 bg-[#16202C] text-white px-6">
        <div className="flex items-center gap-3">
          {firstGroup.map(({ value, icon }, i) => (
            <div key={i} className="flex items-center gap-1">
              <div className="w-5 h-5">{icon}</div>
              <span className="font-bold text-lg">{value ?? "-"}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3">
          {secondGroup.map(({ value, icon }, i) => (
            <div key={i} className="flex items-center gap-1">
              <div className="w-5 h-5">{icon}</div>
              <span className="font-bold text-lg">{value ?? "-"}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative flex bg-[#052432]/70 text-white rounded-xl overflow-hidden transition-all p-3",
        open ? "w-52" : "w-26",
      )}
    >
      <div className="flex flex-col gap-2 flex-1 [&>*:nth-child(2)]:mb-5">
        {items.map(({ label, value, icon, tooltip }) => (
          <div
            key={label}
            className="flex items-center transition-opacity justify-start"
          >
            <Tooltip content={tooltip} delayDuration={150}>
              <div className="w-6 h-6 flex items-center justify-center mr-2">
                {icon}
              </div>
            </Tooltip>
            {open && (
              <div className="flex-1 font-semibold text-sm">{label}</div>
            )}
            <div className="font-semibold text-lg">{value ?? "-"}</div>
          </div>
        ))}
      </div>

      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center ml-4"
        aria-label={open ? "Collapse" : "Expand"}
      >
        <div
          className={cn(
            "transition-transform w-3 h-3",
            open ? "rotate-90" : "rotate-[270deg]",
          )}
        >
          {arrow}
        </div>
      </button>
    </div>
  );
};
